import type * as React from 'react';
import { atomFamily, useRecoilState } from 'recoil';

import type { BlockProps } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { Button, Disclosure, DisclosurePanel, DisclosureTrigger } from '@gitbook/spine-react';

import { BlockLayout } from '../BlockLayout';
import { EditorWidthLayout } from '../contexts';

// We store the state globally so that it's preserved with virtualization
const expandedDiffs = atomFamily<boolean, string>({
    key: 'expandedDiffs',
    default: false,
});

/**
 * Renderer for a diff-unmodified block.
 * By default its content is collapsed.
 */
export function BlockDiffUnmodified(props: BlockProps): React.ReactElement {
    const { node, children, attributes } = props;
    const [isExpanded, setExpanded] = useRecoilState(expandedDiffs(node.key));

    return (
        <BlockLayout renderMode="centered">
            <EditorWidthLayout.View>
                <Disclosure isExpanded={isExpanded} onExpandedChange={setExpanded}>
                    {({ isExpanded }) => (
                        <div {...attributes} className="flex w-full flex-col">
                            {isExpanded ? null : (
                                <DisclosureTrigger>
                                    <Button
                                        label="Expand unchanged content"
                                        kind="blank"
                                        size="medium"
                                        iconOnly
                                        leadingIcon={Icon.Unfold}
                                    />
                                </DisclosureTrigger>
                            )}
                            <DisclosurePanel>{isExpanded ? children : null}</DisclosurePanel>
                        </div>
                    )}
                </Disclosure>
            </EditorWidthLayout.View>
        </BlockLayout>
    );
}
